import { jsx, jsxs } from 'react/jsx-runtime';
import { Check } from '@ruter-ds-next/animations';
import { Label } from '@ruter-ds-next/label';
import { printUnsupportedPropWarning, mergeRefs, useOnParentFormReset } from '@ruter-ds-next/utils';
import classNames from 'classnames';
import { forwardRef, useState, useId, useRef } from 'react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var styles = {"switch":"switch___GXpM-","switch--disabled":"switch--disabled___94UP1","switch__input":"switch__input___gEA3c","switch__label":"switch__label___oyR6D","switch__inputWrapper":"switch__inputWrapper___mjUTv","switch__input--checked":"switch__input--checked___m3EqE","switch__knob":"switch__knob___8fsJ4","switch__input--invalid":"switch__input--invalid___7YhCC","switch__icon":"switch__icon___M03PU"};

let hasPrintedChildrenUnsupportedWarning = false;
let hasPrintedEnabledUnsupportedWarning = false;
let hasPrintedPositionUnsupportedWarning = false;
let hasPrintedSlotPropsButtonUnsupportedWarning = false;
const styleClass = {
  input: (checked, invalid) => classNames(styles.switch__input, {
    [styles['switch__input--checked']]: checked,
    [styles['switch__input--invalid']]: invalid
  }),
  root: disabled => classNames(styles.switch, {
    [styles['switch--disabled']]: disabled
  })
};
const Switch = /*#__PURE__*/forwardRef(function Switch(props, ref) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j;
  if (
  // @ts-expect-error: Property 'children' does not exist on type
  typeof props.children !== 'undefined' && !hasPrintedChildrenUnsupportedWarning) {
    printUnsupportedPropWarning('Switch', 'children', 'Removed in version 6.0.0. Use `label` instead.');
    hasPrintedChildrenUnsupportedWarning = true;
  }
  if (
  // @ts-expect-error: Property 'enabled' does not exist on type
  typeof props.enabled !== 'undefined' && !hasPrintedEnabledUnsupportedWarning) {
    printUnsupportedPropWarning('Switch', 'enabled', 'Removed in version 6.0.0. Use `checked` instead.');
    hasPrintedEnabledUnsupportedWarning = true;
  }
  if (
  // @ts-expect-error: Property 'position' does not exist on type
  typeof props.position !== 'undefined' && !hasPrintedPositionUnsupportedWarning) {
    printUnsupportedPropWarning('Switch', 'position', 'Removed in version 6.0.0. Use `slotProps.root.className` instead.');
    hasPrintedPositionUnsupportedWarning = true;
  }
  if (
  // @ts-expect-error: Property 'button' does not exist on type
  typeof ((_a = props.slotProps) === null || _a === void 0 ? void 0 : _a.button) !== 'undefined' && !hasPrintedSlotPropsButtonUnsupportedWarning) {
    printUnsupportedPropWarning('Switch', 'slotProps.button', 'Removed in version 7.0.0. Use `slotProps.inputWrapper` instead.');
    hasPrintedSlotPropsButtonUnsupportedWarning = true;
  }
  const {
      checked: checkedProp,
      className,
      icon: iconProp,
      id: idProp,
      invalid = false,
      label,
      onChange,
      slotProps
    } = props,
    restProps = __rest(props, ["checked", "className", "icon", "id", "invalid", "label", "onChange", "slotProps"]);
  const [_checked, _setChecked] = useState(undefined);
  const checked = (_b = checkedProp !== null && checkedProp !== void 0 ? checkedProp : _checked) !== null && _b !== void 0 ? _b : false;
  const fallbackId = 'switch-' + useId();
  const id = idProp !== null && idProp !== void 0 ? idProp : fallbackId;
  const _ref = useRef(null);
  const mergedRef = mergeRefs(ref, _ref);
  // prettier-ignore
  const icon = typeof iconProp !== 'undefined' ? iconProp : checked && jsx(Check, {
    className: classNames(styles.switch__icon, (_c = slotProps === null || slotProps === void 0 ? void 0 : slotProps.icon) === null || _c === void 0 ? void 0 : _c.className)
  });
  const onParentFormReset = useOnParentFormReset(_ref);
  onParentFormReset(() => {
    _setChecked(undefined);
  });
  return jsxs("div", {
    // prettier-ignore
    className: classNames(styleClass.root(!!restProps.disabled), (_d = slotProps === null || slotProps === void 0 ? void 0 : slotProps.root) === null || _d === void 0 ? void 0 : _d.className),
    children: [jsxs("div", {
      // prettier-ignore
      className: classNames(styles.switch__inputWrapper, (_e = slotProps === null || slotProps === void 0 ? void 0 : slotProps.inputWrapper) === null || _e === void 0 ? void 0 : _e.className),
      children: [jsx("input", Object.assign({}, restProps, {
        checked: checked,
        // prettier-ignore
        className: classNames(styleClass.input(checked, invalid), className),
        id: id,
        onChange: e => {
          const isUncontrolled = checkedProp === undefined;
          if (isUncontrolled) _setChecked(e.target.checked);
          onChange === null || onChange === void 0 ? void 0 : onChange(e);
        },
        ref: mergedRef,
        type: "checkbox"
      })), jsx("span", {
        // prettier-ignore
        className: classNames(styles.switch__knob, (_f = slotProps === null || slotProps === void 0 ? void 0 : slotProps.knob) === null || _f === void 0 ? void 0 : _f.className),
        children: icon
      })]
    }), label ? jsx(Label, Object.assign({}, slotProps === null || slotProps === void 0 ? void 0 : slotProps.label, {
      // prettier-ignore
      className: classNames(styles.switch__label, (_g = slotProps === null || slotProps === void 0 ? void 0 : slotProps.label) === null || _g === void 0 ? void 0 : _g.className),
      htmlFor: (_j = (_h = slotProps === null || slotProps === void 0 ? void 0 : slotProps.label) === null || _h === void 0 ? void 0 : _h.htmlFor) !== null && _j !== void 0 ? _j : id,
      children: label
    })) : null]
  });
});
Switch.displayName = 'Switch';

export { Switch };
